import { useState } from "react";
import { ChatBubbleLeftEllipsisIcon } from "@heroicons/react/24/outline";
import { Button } from "@gradebook/ui";
import { isSentryEnabled, openFeedbackForm } from "@/lib/sentry";

/**
 * The way to report a problem that did not throw.
 *
 * The `ErrorBoundary` covers the crashes — it has an exception to attach an
 * account to. This covers everything else, which is most of what actually goes
 * wrong in a gradebook: a total that looks wrong, a student missing from a
 * class, a button that silently does nothing. None of those produce an
 * exception, so none of them reach Sentry unless someone says so.
 *
 * Renders nothing when Sentry is not configured, rather than a button that would
 * open nothing.
 */
export function ReportProblemButton() {
  const [opening, setOpening] = useState(false);
  const [failed, setFailed] = useState(false);

  if (!isSentryEnabled()) return null;

  const handleClick = async () => {
    setOpening(true);
    setFailed(false);
    // The feedback form is a lazily-loaded chunk, so this can genuinely fail —
    // an offline tab, a blocked request. Say so instead of leaving a button that
    // appears to do nothing, which is the exact complaint this is here to catch.
    const opened = await openFeedbackForm();
    setOpening(false);
    if (!opened) setFailed(true);
  };

  return (
    <div className="mt-3">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={handleClick}
        disabled={opening}
        className="h-auto w-full justify-start gap-2 px-1 py-1 text-xs font-normal text-muted-foreground hover:text-foreground"
      >
        <ChatBubbleLeftEllipsisIcon className="size-4" />
        {opening ? "Opening…" : "Report a problem"}
      </Button>
      {failed && (
        <p role="alert" className="px-1 text-[11px] text-danger-600">
          Could not open the form — check your connection and try again.
        </p>
      )}
    </div>
  );
}
